import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { Search, Plus } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import CreateShipmentModal from '@/components/shipments/CreateShipmentModal'
import PagyPagination from '@/components/PagyPagination'
import { useOrgPath } from '@/lib/pathFor'
import { formatDate, formatRelativeDate } from '@/lib/formatters'
import { BOOKING_STATUS_COLORS, bookingStatusLabel } from '@/lib/constants'

interface ShipmentClient {
  name: string
}

interface ShipmentItem {
  id: string
  internal_reference: string
  client: ShipmentClient | null
  origin_port: string | null
  destination_port: string | null
  booking_statuses: string[]
  bookings_count: number
  threads_count: number
  last_activity: string | null
  created_at: string
  show_url: string
}

interface PagyData {
  page: number
  pages: number
  count: number
  next: number | null
  prev: number | null
}

interface ShipmentsIndexProps {
  shipments: ShipmentItem[]
  pagy: PagyData
  search: string
  filters: {
    status?: string
    client?: string
    date_from?: string
    date_to?: string
  }
  clients: { id: string; name: string }[]
}

const STATUS_OPTIONS = [
  ['active', 'Active'],
  ['quotes', 'Quotes'],
  ['completed', 'Completed'],
  ['cancelled', 'Cancelled'],
  ['all', 'All'],
] as const

const selectClasses =
  'flex h-8 rounded-md border border-input bg-background px-2 text-[13px] shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer'

export default function ShipmentsIndex({ shipments, pagy, search: initialSearch, filters, clients }: ShipmentsIndexProps) {
  const [search, setSearch] = useState(initialSearch)
  const [status, setStatus] = useState(filters.status || 'active')
  const [client, setClient] = useState(filters.client || '')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const pathFor = useOrgPath()

  function visit(params: { search: string; status: string; client: string }) {
    router.get(pathFor('/shipments'), params, { preserveState: true })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    visit({ search, status, client })
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Shipments</h1>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search Internal Ref, booking ID, client, route, document..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-[240px] pl-8 text-[13px]"
              />
            </div>
          </form>
          <select
            aria-label="Status"
            className={selectClasses}
            value={status}
            onChange={(e) => { setStatus(e.target.value); visit({ search, status: e.target.value, client }) }}
          >
            {STATUS_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            aria-label="Client"
            className={selectClasses}
            value={client}
            onChange={(e) => { setClient(e.target.value); visit({ search, status, client: e.target.value }) }}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <Button
            size="sm"
            className="text-xs"
            onClick={() => setCreateModalOpen(true)}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            New Shipment
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Internal Ref</th>
              <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Client</th>
              <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Route</th>
              <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Bookings</th>
              <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Threads</th>
              <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Last Activity</th>
              <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Created</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map((shipment) => (
              <tr
                key={shipment.id}
                className="border-b hover:bg-muted/30 cursor-pointer"
                onClick={() => router.visit(shipment.show_url)}
              >
                <td className="px-4 py-3">
                  <Link href={shipment.show_url} className="font-mono text-[13px] font-semibold text-foreground hover:underline cursor-pointer">
                    {shipment.internal_reference}
                  </Link>
                </td>
                <td className="px-4 py-3">
                  <Link href={shipment.show_url} className="text-[13px] font-medium text-foreground hover:underline">
                    {shipment.client?.name || '-'}
                  </Link>
                </td>
                <td className="px-4 py-3 text-[13px] text-muted-foreground">
                  {shipment.origin_port && shipment.destination_port
                    ? `${shipment.origin_port} → ${shipment.destination_port}`
                    : shipment.origin_port || shipment.destination_port || '-'}
                </td>
                <td className="px-4 py-3">
                  {shipment.booking_statuses.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {shipment.booking_statuses.map((status, i) => (
                        <Badge
                          key={i}
                          variant="secondary"
                          size="sm"
                          className={`font-medium ${BOOKING_STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}
                        >
                          {bookingStatusLabel(status)}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[13px] text-muted-foreground">{shipment.bookings_count}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[13px] text-muted-foreground">
                  {shipment.threads_count}
                </td>
                <td className="px-4 py-3 text-[13px] text-muted-foreground">
                  {formatRelativeDate(shipment.last_activity)}
                </td>
                <td className="px-4 py-3 text-[13px] text-muted-foreground">
                  {formatDate(shipment.created_at)}
                </td>
              </tr>
            ))}
            {shipments.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No shipments found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <PagyPagination pagy={pagy} />
      </div>

      <CreateShipmentModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        clients={clients}
      />
    </>
  )
}

ShipmentsIndex.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
